import React, {useEffect, useMemo, useState} from "react";
import {ProductListFilterItemSelect} from "@connox/typescript-library";
import {useProductListContext} from "../../ProductListProvider/ProductListProvider";
import {useTranslation} from "react-i18next";
import Select, {SelectOptionType} from "../../../lib/form/Select/Select";
import nlOptionsFormStyles from "../../../newsletter/NewsLetterOptionsForm.module.scss";
import appToggleStyles from "../AppToggleContainer/AppToggleContainer.module.scss";
import styles from "./ProductFilterSelect.module.scss";
import productListFilterstyles from "../ProductListFilter.module.scss";
import {useMediaQuery} from "react-responsive";
import AppToggleContainer from "../AppToggleContainer/AppToggleContainer";
import Checkbox from "../../../lib/form/Checkbox/Checkbox";
import selectStyles from "../../../lib/form/Select/Select.module.scss";
import {CancelFilterButton} from "../../../ShopRating/ShopRatingSection";
import filterReviewStyles from "../FilterReview/FilterReview.module.scss";

interface ProductFilterSelectProps {
  item: ProductListFilterItemSelect;
  onSelect?: (values: string[]) => void;
}

const ProductFilterSelect: React.FC<ProductFilterSelectProps> = ({
  item,
  onSelect = () => {
    return;
  },
}) => {
  const {state, dispatch} = useProductListContext();
  const {t} = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());
  const isMobile = useMediaQuery({query: "(max-width: 480px)"});
  const [open, setOpen] = useState(false);
  const [more, setMore] = useState(7);
  const [localLoading, setLocalLoading] = useState<boolean>(false);

  const [isOptionsSelectedWhileLoading, setIsOptionsSelectedWhileLoading] = useState(false);

  useEffect(() => {
    if (state.filters[item.field]) {
      const filterValue = state.filters[item.field];
      if (Array.isArray(filterValue) && filterValue.length > 0) {
        setSelectedOptions(new Set(filterValue));
      } else {
        setSelectedOptions(new Set());
      }
    } else {
      setSelectedOptions(new Set());
    }
  }, [state.trigger]);

  useEffect(() => {
    if (!state.loading) {
      setLocalLoading(false);
    }
  }, [state.loading]);

  const filterOptions = useMemo((): SelectOptionType[] => {
    const items: SelectOptionType[] = [];
    let rootValues = Array.from(item.rootValues.values());

    // options of certain fields are sorted alphabetically
    if (
      item.field === "hersteller" ||
      item.field === "brands" ||
      item.field === "merken" ||
      item.field === "groupe-de-produits"
    ) {
      rootValues = Array.from(item.rootValues.values()).sort((a, b) => a.id.localeCompare(b.id));
    }

    rootValues.forEach((option) => {
      let count: number;
      const optionValue = Array.from(item.values.values()).find((_option) => _option.id === option.id);

      if (optionValue) {
        count = Number(optionValue.count);
      } else {
        count = Number(option.count);
      }

      items.push({
        value: option.id,
        text: (
          <>
            <span>{String(option.name)}</span>
            {count && count > 0 ? <span className={productListFilterstyles.count}>{count}</span> : ""}
          </>
        ),
      });
    });

    return items;
  }, [Array.from(item.values.values()).map((val) => val.count)]);

  useEffect(() => {
    if (!localLoading) {
      if (isOptionsSelectedWhileLoading) {
        const oldValues = Array.from((state.filters[item.field] ? state.filters[item.field] : []) as string[]).sort();
        const newValues = Array.from(selectedOptions).sort();
        if (oldValues.length === newValues.length && newValues.every((value, index) => value === oldValues[index])) {
          return;
        }
        if (!selectedOptions.size) {
          dispatch({type: "REMOVE_FILTER", payload: {field: item.field}});
        } else {
          dispatch({
            type: "SET_FILTER",
            payload: {field: item.field, value: Array.from(selectedOptions)},
          });
        }

        setIsOptionsSelectedWhileLoading(false);
        setLocalLoading(true);
      }
    }
  }, [localLoading]);

  const onChange = (value: string) => {
    const newSelectedOptions = new Set(selectedOptions);
    if (newSelectedOptions.has(value)) {
      newSelectedOptions.delete(value);
    } else {
      newSelectedOptions.add(value);
    }

    setSelectedOptions(newSelectedOptions);
    const values = Array.from(newSelectedOptions);

    if (isMobile) {
      onSelect(values);

      return;
    }

    if (localLoading) {
      setIsOptionsSelectedWhileLoading(true);

      return;
    }

    if (!values.length) {
      dispatch({type: "REMOVE_FILTER", payload: {field: item.field}});
    } else {
      dispatch({type: "SET_FILTER", payload: {field: item.field, value: values}});
    }

    setLocalLoading(true);
  };

  const isOneOption = filterOptions.length === 1;

  if (!filterOptions.length) {
    return null;
  }

  return (
    <>
      {!isMobile && (
        <div className={appToggleStyles.item}>
          <Select
            value={Array.from(selectedOptions)}
            title={`${item.name || t<string>(item.field)} (${t("{{quantity}} gewählt", {
              quantity: selectedOptions.size,
            })})`}
            styles={{
              wrapper: [nlOptionsFormStyles.select, appToggleStyles.formElementContainer].join(" "),
              selected: styles.selected,
              option: styles.option,
              optionsWrapper: styles.optionsContainer,
              closeArrow: isOneOption ? appToggleStyles.noArrow : appToggleStyles.customArrow,
              openArrow: appToggleStyles.isOpen,
              placeholder: [styles.placeholder, isOneOption ? styles.unselectable : ""].join(" "),
            }}
            onChange={isOneOption ? undefined : onChange}
            options={isOneOption ? [] : filterOptions}
            placeHolder={isOneOption ? filterOptions[0].text : item.name || t(item.field)}
            multi={!isOneOption}
          />
        </div>
      )}

      {isMobile && (
        <>
          <AppToggleContainer
            onClick={() => {
              setOpen(!open);
              setMore(7);
            }}
            isOpening={open}
            icon={"arrow"}
          >
            {`${item.name || t<string>(item.field)}`}
          </AppToggleContainer>
          {!!selectedOptions.size && (
            <div className={styles.filterReviewWrapper}>
              {Array.from(selectedOptions).map((option) => {
                return (
                  <CancelFilterButton
                    className={filterReviewStyles.cancelButtonWrapper}
                    key={option}
                    onCancel={() => {
                      onChange(option);
                    }}
                  >
                    {item.rootValues.get(option)?.name}
                  </CancelFilterButton>
                );
              })}
            </div>
          )}
          {open && (
            <div className={styles.optionsWrapperMobile}>
              {filterOptions.map((option, index) => {
                if (index > more - 1) {
                  return null;
                }

                return (
                  <Checkbox
                    key={option.value}
                    className={selectStyles.checkbox}
                    wrapperClassName={[selectStyles.checkboxWrapper, styles.checkbox].join(" ")}
                    checked={selectedOptions.has(String(option.value))}
                    color={"#1ba1e2"}
                    onChange={() => {
                      onChange(String(option.value));
                    }}
                  >
                    <div className={selectedOptions.has(String(option.value)) ? styles.optionSelectedMobile : ""}>
                      {option.text}
                    </div>
                  </Checkbox>
                );
              })}
              {filterOptions.length > more && (
                <div
                  className={styles.moreBtn}
                  onClick={() => {
                    setMore(filterOptions.length);
                  }}
                >
                  {t("Alle anzeigen")}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ProductFilterSelect;
